import builder from "meta-capi-param-builder-clientjs";
import { canMeasureMarketing, subscribeConsent } from "../measurement/consent";
import { captureUX } from "../measurement/measurement";
import { cleanMetaLandingAttribution, clearMetaCookie, readMetaCookie, resolveFbc, validFbclid, validFbc, validFbp, writeMetaCookie } from "./metaAttribution";

type Identifiers = { fbp: string | null; fbc: string | null };
const empty = (): Identifiers => ({ fbp: null, fbc: null });
const CHECKOUT_COLLECTION_BUDGET_MS = 250;
let current = empty();
let collection: Promise<void> | undefined;
let status: "idle" | "pending" | "ready" | "failed" = "idle";
let epoch = 0;

function refresh(): Identifiers {
  if (!canMeasureMarketing()) return empty();
  const query = new URLSearchParams(window.location.search);
  const fbp = readMetaCookie("fbp") ?? current.fbp;
  const cookieFbc = readMetaCookie("fbc");
  // A new ad click wins; the same click retains its original timestamp.
  const click = validFbclid(query.get("fbclid"));
  const rememberedClick = click && current.fbc?.split(".")[3] === click ? current.fbc : null;
  const fbc = resolveFbc(query.get("fbc") ?? query.get("meta_fbc") ?? rememberedClick, click, cookieFbc ?? current.fbc);
  current = { fbp, fbc };
  if (current.fbc && current.fbc !== cookieFbc) writeMetaCookie("fbc", current.fbc);
  return { ...current };
}

function report(stage: "startup" | "checkout", timedOut = false, checkoutId?: string): void {
  if (!canMeasureMarketing()) return;
  try {
    captureUX("meta_identifier_capture", {
      stage,
      ...(checkoutId ? { checkout_id: checkoutId } : {}),
      meta_fbp_present: !!current.fbp,
      meta_fbc_present: !!current.fbc,
      meta_fbp_persisted: !!readMetaCookie("fbp"),
      meta_collection_status: status,
      meta_collection_timed_out: timedOut,
    });
  } catch { /* Diagnostics must not interfere with payment. */ }
}

/** The bundled SDK writes fbp synchronously, before its in-app click lookup. */
export function startMetaIdentifierCollection(): void {
  if (!canMeasureMarketing() || collection) return;
  const startedEpoch = epoch;
  status = "pending";
  try {
    cleanMetaLandingAttribution(true);
    refresh();
    // No third-party IP lookup. Our backend already captures the client IP.
    const pending = builder.processAndCollectAllParams(window.location.href);
    // Read the cookie directly: fbc's asynchronous lookup may still be pending.
    refresh();
    collection = pending.then(params => {
      if (startedEpoch !== epoch || !canMeasureMarketing()) {
        // Native lookups cannot be cancelled. Discard their late writes if the
        // tracking state changed while they were running.
        for (const name of ["fbp", "fbc"] as const) {
          const value = canMeasureMarketing() ? current[name] : null;
          if (value) writeMetaCookie(name, value);
          else clearMetaCookie(name);
        }
        return;
      }
      current.fbp = validFbp(params._fbp) ?? current.fbp;
      const collectedFbc = validFbc(params._fbc);
      // An in-app lookup that started on landing must not overwrite a newer
      // click captured while that native lookup was still pending.
      if (current.fbc && collectedFbc && current.fbc.split(".")[3] !== collectedFbc.split(".")[3]) {
        writeMetaCookie("fbc", current.fbc);
      } else {
        current.fbc = collectedFbc ?? current.fbc;
      }
      if (!readMetaCookie("fbc")) clearMetaCookie("fbc");
      status = "ready";
      refresh();
      report("startup");
    }).catch(() => {
      if (startedEpoch !== epoch) return;
      status = "failed";
      refresh();
      report("startup");
    });
  } catch {
    status = "failed";
    collection = Promise.resolve();
    report("startup");
  }
}

export function getMetaIdentifiers(): Identifiers {
  if (!canMeasureMarketing()) return empty();
  try { startMetaIdentifierCollection(); return refresh(); } catch { return { ...current }; }
}

/** Refresh at the API boundary, after any time spent filling in payment details. */
export async function getMetaCheckoutIdentifiers(checkoutId?: string): Promise<Identifiers> {
  let ids = getMetaIdentifiers();
  let timedOut = false;
  // Usually fbc already came from the landing URL. Only give an unfinished
  // in-app lookup a short budget when we don't have a click yet.
  if (canMeasureMarketing() && !ids.fbc && status === "pending" && collection) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        collection,
        new Promise<void>(resolve => { timer = setTimeout(() => { timedOut = true; resolve(); }, CHECKOUT_COLLECTION_BUDGET_MS); }),
      ]);
    } finally { if (timer !== undefined) clearTimeout(timer); }
    ids = getMetaIdentifiers();
  }
  report("checkout", timedOut, checkoutId);
  return ids;
}

subscribeConsent(() => {
  if (canMeasureMarketing()) return;
  epoch++;
  current = empty();
  collection = undefined;
  status = "idle";
});
